import { createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { API, SCOPE_EVENTS_MANAGEMENT } from '../../constants';
import { IS_AFTER, IS_BETWEEN } from '../../constants/filter';
import { INITIAL_LIST_STATE_EXTENDED } from '../helpers/common';
import { extendListBuilder, generateListActions } from '../helpers/listActionsHelpers';
import { application } from '../../services/application';
import { onRejectAlert } from '../helpers/sharedCases';

export const INITIAL_STATE = {
  ...INITIAL_LIST_STATE_EXTENDED,
  order: ['startAt', 'asc'],
  calendar: { items: [], pending: false, month: null },
};

//Actions
const listActions = generateListActions({
  scope: SCOPE_EVENTS_MANAGEMENT,
  apiMethod: {
    GET_LIST: API.EVENTS_MANAGEMENT.GET_LIST,
  },
  getStore: (store) => store.eventsManagement,
});

const deleteEvent = createAsyncThunk('eventsManagement/deleteEvent', async (params) => {
  return application.call(API.EVENTS_MANAGEMENT.DELETE, params);
});
function onDeleteEvent(state, action) {
  const copy = [...state.items];
  const index = copy.findIndex(i => i.id === action.payload.id);
  if (index !== -1) {
    copy.splice(index, 1);
    Object.assign(state, {
      items: [...copy],
      count: state.count - 1,
    });
  }
}

const duplicateEvent = createAsyncThunk('eventsManagement/duplicateEvent', async (params) => {
  return application.call(API.EVENTS_MANAGEMENT.DUPLICATE, params);
});

const createEvent = createAsyncThunk('eventsManagement/createEvent', async (params) => {
  return application.call(API.EVENTS_MANAGEMENT.CREATE, params);
});

const updateEvent = createAsyncThunk('eventsManagement/updateEvent', async (params) => {
  return application.call(API.EVENTS_MANAGEMENT.UPDATE, params);
});

const CALENDAR_PAGE_SIZE = 200;

const fetchCalendarEvents = createAsyncThunk(
  'eventsManagement/fetchCalendarEvents',
  async (month, { getState }) => {
    const targetMonth = month || getState().eventsManagement.calendar.month;
    const monthStart = dayjs(`${targetMonth}-01`).startOf('month');
    const monthEnd = dayjs(`${targetMonth}-01`).endOf('month');
    const result = await application.call(API.EVENTS_MANAGEMENT.GET_LIST, {
      filters: { startAt: { type: IS_BETWEEN, value: [monthStart.toISOString(), monthEnd.toISOString()] } },
      order: ['startAt', 'asc'],
      pagination: { page: 1, perPage: CALENDAR_PAGE_SIZE },
    });
    return { ...result, month: targetMonth };
  },
  {
    condition: (month, { getState }) => Boolean(month || getState().eventsManagement.calendar.month),
  }
);

const initCalendar = createAsyncThunk('eventsManagement/initCalendar', async (_, { dispatch }) => {
  const result = await application.call(API.EVENTS_MANAGEMENT.GET_LIST, {
    filters: { startAt: { type: IS_AFTER, value: dayjs().toISOString() } },
    order: ['startAt', 'asc'],
    pagination: { page: 1, perPage: 1 },
  });
  const earliest = result.items[0];
  const month = earliest ? dayjs(earliest.startAt).format('YYYY-MM') : dayjs().format('YYYY-MM');
  dispatch(fetchCalendarEvents(month));
  return { month };
});

export const actions = {
  ...listActions,
  deleteEvent,
  duplicateEvent,
  createEvent,
  updateEvent,
  fetchCalendarEvents,
  initCalendar,
};

export const selectCalendar = (state) => state.eventsManagement.calendar;

function onCalendarPending(state, action) {
  state.calendar.pending = true;
  state.calendar.month = action.meta?.arg || state.calendar.month;
}
function onCalendarFulfilled(state, action) {
  state.calendar.items = action.payload.items;
  state.calendar.month = action.payload.month;
  state.calendar.pending = false;
}
function onCalendarRejected(state, action) {
  state.calendar.pending = false;
  onRejectAlert(state, action);
}

export default createReducer(INITIAL_STATE, (builder) => {
  return extendListBuilder(builder, actions)
    .addCase(deleteEvent.fulfilled, onDeleteEvent)
    .addCase(deleteEvent.rejected, onRejectAlert)
    .addCase(duplicateEvent.rejected, onRejectAlert)
    .addCase(createEvent.rejected, onRejectAlert)
    .addCase(updateEvent.rejected, onRejectAlert)
    .addCase(fetchCalendarEvents.pending, onCalendarPending)
    .addCase(fetchCalendarEvents.fulfilled, onCalendarFulfilled)
    .addCase(fetchCalendarEvents.rejected, onCalendarRejected)
    .addCase(initCalendar.pending, onCalendarPending)
    .addCase(initCalendar.rejected, onCalendarRejected);
});
